import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Trophy, Gift } from "lucide-react";
import api from "@/lib/api";

const HIDE_ON = ["/admin", "/dashboard", "/login", "/register", "/two-factor", "/staff-setup", "/auth"];
const THEME = {
    christmas: { bg: "from-rose-700 via-red-600 to-emerald-700", emoji: "🎄" },
    newyear: { bg: "from-indigo-800 via-violet-700 to-fuchsia-600", emoji: "🎆" },
    default: { bg: "from-navy via-slate-800 to-emerald-700", emoji: "🏆" },
};

export function useCountdown(iso) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
    const ms = Math.max(0, new Date(iso).getTime() - now);
    const d = Math.floor(ms / 864e5), h = Math.floor(ms / 36e5) % 24, m = Math.floor(ms / 6e4) % 60, s = Math.floor(ms / 1e3) % 60;
    return { ms, d, h, m, s };
}

export function Countdown({ to, light }) {
    const { d, h, m, s } = useCountdown(to);
    return (
        <div className="flex gap-2 justify-center">
            {[["days", d], ["hrs", h], ["min", m], ["sec", s]].map(([l, v]) => (
                <div key={l} className={`rounded-xl px-3 py-2 min-w-[56px] text-center ${light ? "bg-white/15 text-white" : "bg-slate-100 text-slate-800"}`}>
                    <div className="text-xl font-bold tabular-nums">{String(v).padStart(2, "0")}</div>
                    <div className="text-[10px] uppercase tracking-wider opacity-75">{l}</div>
                </div>
            ))}
        </div>
    );
}

// Shows the live contest on every page load (each refresh) on public pages; after closing, a small button reopens it.
export default function ContestPopup() {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [c, setC] = useState(null);
    const [open, setOpen] = useState(false);
    const hidden = HIDE_ON.some((p) => pathname.startsWith(p));

    useEffect(() => {
        api.get("/public/contest").then((r) => {
            const k = r.data?.contest;
            if (!k || !k.popup || k.phase === "ended") return;
            setC(k);
            setTimeout(() => setOpen(true), 1500);
        }).catch(() => {});
    }, []);

    if (!c || hidden) return null;
    const t = THEME[c.theme] || THEME.default;
    const close = () => setOpen(false);
    const go = () => { close(); navigate("/dashboard/contest"); };
    const money = (v) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

    if (!open) {
        return (
            <button onClick={() => setOpen(true)} data-testid="contest-pill"
                className={`fixed bottom-4 left-4 z-40 inline-flex items-center gap-2 text-white text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg bg-gradient-to-r ${t.bg}`}>
                <Gift size={16} /> Win {money(c.prize_total)}
            </button>
        );
    }
    return (
        <div className="fixed inset-0 z-[70] bg-slate-900/60 flex items-center justify-center p-4" onClick={close}>
            <div onClick={(e) => e.stopPropagation()} data-testid="contest-popup" role="dialog" aria-modal="true" aria-label={c.title}
                className={`relative w-full max-w-md rounded-3xl text-white text-center p-6 pt-8 shadow-2xl bg-gradient-to-br ${t.bg}`}>
                <button onClick={close} aria-label="Close" className="absolute top-3 right-3 text-white/80 hover:text-white"><X size={22} /></button>
                <div className="text-5xl mb-2" aria-hidden>{t.emoji}</div>
                <h2 className="font-heading text-2xl font-bold leading-tight">{c.title}</h2>
                {c.subtitle && <p className="text-white/85 text-sm mt-1">{c.subtitle}</p>}
                <div className="my-4">
                    <div className="text-xs uppercase tracking-widest text-white/75">Total prizes</div>
                    <div className="text-5xl font-extrabold">{money(c.prize_total)}<span className="text-lg font-semibold ml-1">{c.currency}</span></div>
                </div>
                <div className="flex justify-center gap-2 mb-4 flex-wrap">
                    {(c.prizes || []).map((p, i) => (
                        <span key={i} className="bg-white/15 rounded-full px-3 py-1 text-sm font-semibold">{["🥇", "🥈", "🥉"][i] || `#${i + 1}`} {money(p)}</span>
                    ))}
                </div>
                <div className="text-xs text-white/80 mb-2">{c.phase === "upcoming" ? "Starts in" : "Ends in"}</div>
                <Countdown to={c.phase === "upcoming" ? c.start : c.end} light />
                <p className="text-sm text-white/85 mt-4">Sell gift cards during the contest. The biggest sellers win.</p>
                <button onClick={go} data-testid="contest-popup-go"
                    className="mt-4 w-full bg-white text-slate-900 font-bold py-3.5 rounded-full inline-flex items-center justify-center gap-2 hover:bg-amber-50">
                    <Trophy size={18} /> See my position
                </button>
            </div>
        </div>
    );
}
